// Admin re-run path for the first-run setup wizard (item 76 of
// PRODUCT-ROADMAP.md). The fresh-install auto-redirect path lives at
// /setup and uses its own starfield-backed shell; this page wraps the
// same SetupWizard component in the AdminShell so an admin can walk
// through the steps again without bouncing the auto-redirect bit.
//
// The "Reset wizard state" action clears every timestamp on
// system_setup so the public-config endpoint flips
// `setup_required: true` again, useful when an admin wants to
// rehearse the on-boarding flow before a customer demo.

import { useState } from 'react';
import { CheckCircle2, RotateCcw, AlertTriangle, Loader2 } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { SetupWizard } from '../SetupWizard';
import { useSetupStatus, resetSetup } from '../../api/setup';
import { usePublicConfig } from '../../api/publicConfig';

export function AdminSystemSetup() {
  const { data: status, mutate: mutateStatus } = useSetupStatus();
  const { mutate: mutatePubConfig } = usePublicConfig();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const onReset = async () => {
    if (!confirm('Reset wizard state? The next page load on a fresh tab will auto-redirect to /setup.')) return;
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await resetSetup();
      await Promise.all([mutateStatus(), mutatePubConfig()]);
      setMsg('Wizard state cleared. Visit /setup or open a fresh tab to walk through it again.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const completed = !!status?.setup_completed_at;

  return (
    <AdminShell
      title="Setup wizard"
      description="Re-run the first-run wizard, or reset its state so the next fresh tab auto-redirects to /setup."
    >
      <Card>
        <CardHeader title="State" />
        <CardBody className="space-y-3">
          <div className="text-sm text-fg-muted flex items-center gap-2">
            {completed ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-success" />
                Completed{' '}
                <span className="font-mono">
                  {status?.setup_completed_at
                    ? new Date(status.setup_completed_at).toISOString().slice(0, 16).replace('T', ' ')
                    : ''}
                </span>{' '}
                UTC
                {status?.completed_by ? (
                  <>
                    {' '}by <span className="font-mono">{status.completed_by}</span>
                  </>
                ) : null}
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 text-warning" />
                Not yet completed — fresh sessions auto-redirect to{' '}
                <span className="font-mono">/setup</span>.
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={onReset} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              {busy ? 'Resetting…' : 'Reset wizard state'}
            </Button>
            {msg && <span className="text-xs text-success">{msg}</span>}
            {err && <span className="text-xs text-danger font-mono break-all">{err}</span>}
          </div>
        </CardBody>
      </Card>
      <div className="mt-6">
        <SetupWizard embedded />
      </div>
    </AdminShell>
  );
}
